import { Action, createReducer, on } from "@ngrx/store";

import * as PartiesActionTypes from "../../actions/parties/parties.actions";

import { PaginationRequest } from "src/app/models/common/paginationRequest";
import { CustomerPartyDTO } from "src/app/models/Party/customerPartyDTO";
import { EmployeePartyDTO } from "src/app/models/Party/employeePartyDTO";
import { PartyDTO } from "src/app/models/Party/partyDTO";
import { PartySalutationDTO } from "src/app/models/Party/partySalutationDTO";
import { SupplierPartyDTO } from "src/app/models/Party/supplierPartyDTO";
import { CountryDTO } from "src/app/models/Country/countryDTO";
import { CategoryDTO } from "src/app/models/Categories/categoryDTO";
import { CategoryElementDTO } from "src/app/models/Categories/categoryElementDTO";
import { PropertyDTO } from "src/app/models/Properties/propertyDTO";
import { ColumnSorterInfo } from "src/app/models/custom-table/custom-table.model";

export interface PartiesState {
  salutations: PartySalutationDTO[];
  countries: CountryDTO[];
  partyList?: PartyDTO[];
  party: any;
  customerParty: CustomerPartyDTO;
  supplierParty: SupplierPartyDTO;
  employeeParty: EmployeePartyDTO;
  created: boolean;
  isLoading: boolean;
  isLoadingCustomerSearch: boolean;
  error: Error;
  totalRecords: number;
  pageReq: PaginationRequest;
  categories: CategoryDTO[];
  optionalCategories: CategoryDTO[];
  categoryElements: CategoryElementDTO[];
  properties: PropertyDTO[];
  optionalProperties: PropertyDTO[];
  customerPartyList: any; // CustomCustomerDTO[];
  employeePartyList: any; // CustomEmployeeDTO[];
  supplierPartyList: any; // CustomSupplierDTO[];
  categoriesList: CategoryDTO[];
  propertiesList: PropertyDTO[];
  vehicleListByCustomer?: any;
  employeesByCustomer?: any;
  excelCustomerList?: any[];
  excelEmployeeList?: any[];
  excelSupplierList?: any[];
  totalCustomers?: number;
  totalEmployees?: number;
  totalSuppliers?: number;
  customerTableConfig: ColumnSorterInfo[],
  employeeTableConfig: ColumnSorterInfo[],
  supplierTableConfig: ColumnSorterInfo[],
}

export const initialState: PartiesState = {
  salutations: null,
  countries: null,
  partyList: null,
  party: null,
  customerParty: null,
  supplierParty: null,
  employeeParty: null,
  created: false,
  isLoading: false,
  isLoadingCustomerSearch: false,
  error: null,
  totalRecords: 0,
  pageReq: null,
  categories: null,
  categoryElements: null,
  properties: null,
  optionalCategories: null,
  optionalProperties: null,
  customerPartyList: null,
  categoriesList: null,
  propertiesList: null,
  employeePartyList: null,
  supplierPartyList: null,
  vehicleListByCustomer: null,
  employeesByCustomer: null,
  excelCustomerList: null,
  excelEmployeeList: null,
  excelSupplierList: null,
  totalCustomers: null,
  totalEmployees: null,
  totalSuppliers: null,
  customerTableConfig: null,
  employeeTableConfig: null,
  supplierTableConfig: null,
};

const partiesReducer = createReducer(
  initialState,
  on(PartiesActionTypes.resetPartyState, () => initialState),
  on(PartiesActionTypes.resetCustomerEmployeesListState, (state) => {
    return {
      ...state,
      employeePartyList: null,
      totalEmployees: null,
    }
  }),
  on(
    PartiesActionTypes.getSalutations,
    (state, { pageReq }) =>
      ({
        ...state,
        error: null,
        pageReq: pageReq,
      } as PartiesState)
  ),
  on(PartiesActionTypes.loadSalutations, (state, { response }) => {
    let currentOffset = state.pageReq ? state.pageReq.offset : 0;
    let totalRecords = Math.max(
      currentOffset + (response as any).result.size,
      state.totalRecords
    );
    return {
      ...state,
      salutations: (response as any).result.items || [],
      error: null,
      totalRecords: totalRecords,
    };
  }),
  on(
    PartiesActionTypes.getCustomerVehiclesList,
    (state, { container, options }) =>
      ({
        ...state,
        error: null,
        container: container,
        options: options,
      } as PartiesState)
  ),
  on(PartiesActionTypes.loadCustomerVehiclesList, (state, { response }) => {
    let currentOffset = state.pageReq ? state.pageReq.offset : 0;
    let totalRecords = Math.max(
      currentOffset + (response as any).result.size,
      state.totalRecords
    );
    return {
      ...state,
      vehicleListByCustomer: (response as any).result || [],
      error: null,
      totalRecords: totalRecords,
    };
  }),
  on(
    PartiesActionTypes.getCustomerEmployeesList,
    (state, { container, options }) =>
      ({
        ...state,
        error: null,
        container: container,
        options: options,
      } as PartiesState)
  ),
  on(PartiesActionTypes.loadCustomerEmployeesList, (state, { response }) => {
    let currentOffset = state.pageReq ? state.pageReq.offset : 0;
    let totalRecords = Math.max(
      currentOffset + (response as any).result.size,
      state.totalRecords
    );
    return {
      ...state,
      employeesByCustomer: (response as any).result || [],
      error: null,
      totalRecords: totalRecords,
    };
  }),
  on(PartiesActionTypes.loadCountries, (state, { response }) => {
    let currentOffset = state.pageReq ? state.pageReq.offset : 0;
    let totalRecords = Math.max(
      currentOffset + (response as any).result.size,
      state.totalRecords
    );
    return {
      ...state,
      countries: (response as any).result.items || [],
      error: null,
      totalRecords: totalRecords,
    };
  }),
  on(PartiesActionTypes.loadCategoriesByUsage, (state, { response }) => {
    let currentOffset = state.pageReq ? state.pageReq.offset : 0;
    let totalRecords = Math.max(
      currentOffset + (response as any).result.size,
      state.totalRecords
    );
    return {
      ...state,
      categories: (response as any).result.items || [],
      error: null,
      totalRecords: totalRecords,
    };
  }),
  on(PartiesActionTypes.loadPropertiesByUsage, (state, { response }) => {
    let currentOffset = state.pageReq ? state.pageReq.offset : 0;
    let totalRecords = Math.max(
      currentOffset + (response as any).result.size,
      state.totalRecords
    );
    return {
      ...state,
      properties: (response as any).result.items || [],
      error: null,
      totalRecords: totalRecords,
    };
  }),
  on(PartiesActionTypes.setParty, (state, { party }) => ({
    ...state,
    party: (party as any).result,
    error: null,
  })),
  on(PartiesActionTypes.setCustomerParty, (state, { party }) => ({
    ...state,
    customerParty: (party as any).result,
    error: null,
  })),
  on(PartiesActionTypes.setSupplierParty, (state, { party }) => ({
    ...state,
    supplierParty: (party as any).result,
    error: null,
  })),
  on(PartiesActionTypes.setEmployeeParty, (state, { party }) => ({
    ...state,
    employeeParty: (party as any).result,
    error: null,
  })),
  on(PartiesActionTypes.loadCategoryElements, (state, { response }) => ({
    ...state,
    categoryElements: (response as any).result,
    error: null,
  })),
  on(
    PartiesActionTypes.loadOptionalCategoriesByUsage,
    (state, { response }) => {
      let currentOffset = state.pageReq ? state.pageReq.offset : 0;
      let totalRecords = Math.max(
        currentOffset + (response as any).result.size,
        state.totalRecords
      );
      return {
        ...state,
        optionalCategories: (response as any).result.items || [],
        error: null,
        totalRecords: totalRecords,
      };
    }
  ),
  on(
    PartiesActionTypes.loadOptionalPropertiesByUsage,
    (state, { response }) => {
      let currentOffset = state.pageReq ? state.pageReq.offset : 0;
      let totalRecords = Math.max(
        currentOffset + (response as any).result.size,
        state.totalRecords
      );
      return {
        ...state,
        optionalProperties: (response as any).result.items || [],
        error: null,
        totalRecords: totalRecords,
      };
    }
  ),
  on(PartiesActionTypes.resetCustomerParty, (state) => {
    return {
      ...state,
      customerParty: null,
      error: null,
    };
  }),
  on(PartiesActionTypes.resetSupplierParty, (state) => {
    return {
      ...state,
      supplierParty: null,
      error: null,
    };
  }),
  on(PartiesActionTypes.resetEmployeeParty, (state) => {
    return {
      ...state,
      employeeParty: null,
      error: null,
    };
  }),
  on(PartiesActionTypes.getCustomerPartyList, (state, { options }) => ({
    ...state,
    options,
    isLoading: true,
  })),
  on(PartiesActionTypes.getCustomerPartyListSearch, (state, { options }) => ({
    ...state,
    options,
    isLoadingCustomerSearch: true,
  })),
  on(PartiesActionTypes.loadCustomerPartyList, (state, { response }) => ({
    ...state,
    customerPartyList: (response as any)?.result?.items || undefined,
    error: null,
    isLoading: false,
    isLoadingCustomerSearch: false,
    totalCustomers: (response as any)?.result?.size
      ? (response as any)?.result?.size
      : 0,
  })),
  on(
    PartiesActionTypes.getCustomerTableConfig, 
    PartiesActionTypes.getCategoriesForListByUsage,
    PartiesActionTypes.getPropertiesForListByUsage,
    (state) => {
    return {
      ...state,
      isLoading: true,
    }
  }),
  on(PartiesActionTypes.loadCustomerTableConfig, (state, { response }) => {
    return {
      ...state,
      customerTableConfig: (response as any)?.result?.value?.tableColumnInfo ?? undefined,
    };
  }),
  on(PartiesActionTypes.saveCustomerTableConfig, (state) => {
    return {
      ...state,
      isLoading: true,
    };
  }),

  on(PartiesActionTypes.loadCategoriesForListByUsage, (state, { response }) => {
    return {
      ...state,
      categoriesList: (response as any).result.items || [],
      error: null,
    };
  }),
  on(PartiesActionTypes.loadPropertiesForListByUsage, (state, { response }) => {
    let currentOffset = state.pageReq ? state.pageReq.offset : 0;
    let totalRecords = Math.max(
      currentOffset + (response as any).result.size,
      state.totalRecords
    );
    return {
      ...state,
      propertiesList: (response as any).result.items || [],
      error: null,
      totalRecords: totalRecords,
    };
  }),
  on(PartiesActionTypes.setLoadingStartState, (state) => {
    return {
      ...state,
      isLoading: true,
      error: null,
    };
  }),
  on(PartiesActionTypes.setLoadingEndState, (state) => {
    return {
      ...state,
      isLoading: false,
      error: null,
    };
  }),
  on(PartiesActionTypes.getEmployeePartyList, (state, { options }) => ({
    ...state,
    options,
    isLoading: true,
  })),
  on(PartiesActionTypes.loadEmployeePartyList, (state, { response }) => ({
    ...state,
    employeePartyList: (response as any)?.result?.items || undefined,
    error: null,
    isLoading: false,
    totalEmployees: (response as any)?.result?.size
      ? (response as any)?.result?.size
      : 0,
  })),
  on(PartiesActionTypes.getEmployeeTableConfig, (state) => {
    return {
      ...state,
      isLoading: true,
    }
  }),
  on(PartiesActionTypes.loadEmployeeTableConfig, (state, { response }) => {
    return {
      ...state,
      employeeTableConfig: (response as any)?.result?.value?.tableColumnInfo ?? undefined,
    };
  }),
  on(PartiesActionTypes.saveEmployeeTableConfig, (state) => {
    return {
      ...state,
      isLoading: true,
    };
  }),
  on(PartiesActionTypes.getSupplierPartyList, (state, { options }) => ({
    ...state,
    options,
    isLoading: true,
  })),
  on(PartiesActionTypes.loadSupplierPartyList, (state, { response }) => ({
    ...state,
    supplierPartyList: (response as any)?.result?.items || undefined,
    error: null,
    isLoading: false,
    totalSuppliers: (response as any)?.result?.size
      ? (response as any)?.result?.size
      : 0,
  })),
  on(PartiesActionTypes.getSupplierTableConfig, (state) => {
    return {
      ...state,
      isLoading: true,
    }
  }),
  on(PartiesActionTypes.loadSupplierTableConfig, (state, { response }) => {
    return {
      ...state,
      supplierTableConfig: (response as any)?.result?.value?.tableColumnInfo ?? undefined,
    };
  }),
  on(PartiesActionTypes.saveSupplierTableConfig, (state) => {
    return {
      ...state,
      isLoading: true,
    };
  }),
  on(
    PartiesActionTypes.getCustomerVehiclesList,
    (state, { container, options }) =>
      ({
        ...state,
        error: null,
        container: container,
        options: options,
      } as PartiesState)
  ),
  on(PartiesActionTypes.loadCustomerVehiclesList, (state, { response }) => {
    let currentOffset = state.pageReq ? state.pageReq.offset : 0;
    let totalRecords = Math.max(
      currentOffset + (response as any).result.size,
      state.totalRecords
    );
    return {
      ...state,
      vehicleListByCustomer: (response as any).result || [],
      error: null,
      totalRecords: totalRecords,
    };
  }),
  on(
    PartiesActionTypes.getCustomerExcelList,
    (state, { options }) =>
      ({
        ...state,
        error: null,
        options: options,
        isLoading: true,
      } as PartiesState)
  ),
  on(PartiesActionTypes.loadCustomerExcelList, (state, { response }) => {
    return {
      ...state,
      excelCustomerList: (response as any)?.result?.items
        ? (response as any)?.result?.items
        : undefined,
      isLoading: false,
      error: null,
    };
  }),
  on(
    PartiesActionTypes.getEmployeeExcelList,
    (state, { options }) =>
      ({
        ...state,
        error: null,
        options: options,
        isLoading: true,
      } as PartiesState)
  ),
  on(PartiesActionTypes.loadEmployeeExcelList, (state, { response }) => {
    return {
      ...state,
      excelEmployeeList: (response as any)?.result?.items
        ? (response as any)?.result?.items
        : undefined,
      isLoading: false,
      error: null,
    };
  }),
  on(
    PartiesActionTypes.getSupplierExcelList,
    (state, { options }) =>
      ({
        ...state,
        error: null,
        options: options,
        isLoading: true,
      } as PartiesState)
  ),
  on(PartiesActionTypes.loadSupplierExcelList, (state, { response }) => {
    return {
      ...state,
      excelSupplierList: (response as any)?.result?.items
        ? (response as any)?.result?.items
        : undefined,
      isLoading: false,
      error: null,
    };
  }),
  on(
    PartiesActionTypes.getCustomerParty,
    PartiesActionTypes.getEmployeeParty,
    PartiesActionTypes.getSupplierParty,
    (state) => {
    return {
      ...state,
      isLoading: true,
      created: false,
      error: null,
    };
  }),
  on(PartiesActionTypes.resetCreation, (state) => {
    return {
      ...state,
      created: true,
    };
  }),
  on(
    PartiesActionTypes.createCustomerParty,
    PartiesActionTypes.updateCustomerParty,
    PartiesActionTypes.createEmployeeParty,
    PartiesActionTypes.updateEmployeeParty,
    PartiesActionTypes.createSupplierParty,
    PartiesActionTypes.updateSupplierParty,
    (state) => {
      return {
        ...state,
        isLoading: true,
      };
    }
  )
);

export function reducer(state: PartiesState | undefined, action: Action) {
  return partiesReducer(state, action);
}
